import { z } from 'zod';
import { imageWithUploadSchema } from './image.schema';
import { countriesSupportedSchema } from './country.schema';
import {
  dateSchema,
  idSchema,
  nameSchema,
  optionalSparseSchema,
} from '../validation';
export const licenseTypeSchema = z.enum(['carrier_license', 'broker_license']);
export type licenseType = z.infer<typeof licenseTypeSchema>;

/**
 * Legal form / commercial registration type. Used by underwriters
 * to understand which corporate framework an org operates under.
 *
 * - `llc`                  — Limited Liability Company (ذ.م.م) — most common SME
 *                            structure in KSA.
 * - `establishment`        — مؤسسة فردية — sole proprietorship.
 * - `simple_joint_stock`   — مساهمة مبسطة — Saudi "simple JSC" introduced under
 *                            the new Companies Law.
 * - `joint_stock`          — شركة مساهمة — full joint stock company.
 * - `partnership`          — شركة تضامن — general partnership.
 * - `branch_foreign`       — Saudi branch of a foreign entity.
 * - `other`                — anything else; the underwriter will ask.
 *
 * Values are stable IDs used as i18n keys and in audit logs;
 * **never** rename, only deprecate by introducing a successor.
 */
export const businessTypeSchema = z.enum([
  'llc',
  'establishment',
  'simple_joint_stock',
  'joint_stock',
  'partnership',
  'branch_foreign',
  'other',
]);
export type BusinessType = z.infer<typeof businessTypeSchema>;

/**
 * Local alias for the shared blank-tolerant optional wrapper — every
 * `legal` field is an optional form input, so the name reads better
 * inline than the fully-qualified one.
 */
const optionalSparse = optionalSparseSchema;

/**
 * Business / carrier / org legal info.
 *
 * **All fields are optional.** A legal entity may exist with only a
 * subset of the documents filled in (e.g. an Org has uploaded the CR
 * but hasn't entered the tax id yet). Readers MUST guard every access
 * (`legal?.registrationId`); writers SHOULD send only the fields the
 * user actually filled in.
 *
 * The grouping (registration / license / tax / address) is purely
 * documentary — every group is independently optional, and individual
 * fields within a group are also independent (e.g. a stored entity may
 * carry `registrationId` without `registrationImage`).
 */
export const legalEntitySchema = z.object({
  //* registration

  /** Company CR Image */
  registrationImage: imageWithUploadSchema.optional(),
  /**
   * Company CR Id. CR Number.
   *
   * Also what the Bayan connector sends as `carrier.moi` for a company
   * carrier — Saudi establishments identify themselves with the same
   * registration number, so there is no separate "700 number" field.
   */
  registrationId: optionalSparse(idSchema),
  /** The name of the company written in the Company CR. This is the Legal Name of the entity */
  registrationName: optionalSparse(nameSchema),

  /** the date in which the registration will expire */
  registrationExpiryDate: dateSchema.optional(),

  //* license
  licenseImage: imageWithUploadSchema.optional(),
  /** some organizations have special licenses such as  `carrier_license` to transport goods.
   *
   * that's this is optional be cause sometimes we will need it.
   */
  licenseType: licenseTypeSchema.optional(),
  licenseId: optionalSparse(idSchema),
  licenseExpiryDate: dateSchema.optional(),

  //* tax

  /** company tax id */
  taxId: optionalSparse(idSchema),

  /** company tax image */
  taxImage: imageWithUploadSchema.optional(),

  //* entity meta — additive (relations-v2 financing pilot, 2026-06)

  /**
   * Legal form of the company (LLC, sole proprietorship, simple
   * joint stock, …). Optional because most existing rows pre-date
   * the field. Lenders use this to choose the right contract
   * template; analytics use it to slice underwriting funnel.
   *
   * Stored as an enum string (snake_case, see
   * {@link businessTypeSchema}). Translations live in `c-main` /
   * `m-org` under `business_type.<key>`.
   */
  businessType: businessTypeSchema.optional(),

  /**
   * Year the entity was established (Gregorian). Optional. Stored
   * as a 4-digit integer (not a Date) because the day / month is
   * almost never known and we want clean filterability ("show me
   * orgs older than 5 years").
   *
   * Range guard: 1900..currentYear; rejecting wild inputs catches
   * the common typo (e.g. "20023") without making the field
   * harder to fill in than necessary.
   */
  establishedYear: z
    .number()
    .int()
    .min(1900)
    .max(new Date().getFullYear())
    .optional(),

  //* address

  /** company address */
  fullAddress: optionalSparse(nameSchema),

  // @TBC -- we might make this a dropdown not a random string
  addressCity: optionalSparse(nameSchema),
  addressCountry: countriesSupportedSchema.optional(),
  /** company proof of address */
  addressImage: imageWithUploadSchema.optional(),
});
/**  business or carrier legal info */
export type LegalEntity = z.infer<typeof legalEntitySchema>;
